import { KEYCODE_MAP_HFD_LAYOUT } from "./keyCodeMapperHfd";
import { resolveHfdFnFuncDisplayKeyCode } from "./hfdFnDisplay";

/** matrix (row,col) → Fn 层展示用 keyCode（对齐 X98 (1K) 出厂 Fn 图 / 竞品驱动） */
const X98_1K_FN_CELL_OVERRIDES: Readonly<Record<string, number>> = {
  // Row 0 — Esc / F 行
  "0,0": resolveHfdFnFuncDisplayKeyCode(4096, { v2: true }), // Esc 恢复出厂设置
  // Row 1 — `~123… Backspace·Ins
  "1,0": resolveHfdFnFuncDisplayKeyCode(63, { v2: false }), // `~ GIF模式
  "1,13": resolveHfdFnFuncDisplayKeyCode(12294, { v2: true }), // Backspace 关闭灯光
  // Row 2 — Tab / QWER … \| [ ] PgUp
  "2,0": resolveHfdFnFuncDisplayKeyCode(93, { v2: false }), // Tab 一键白光
  "2,1": resolveHfdFnFuncDisplayKeyCode(8192, { v2: true }), // Q 蓝牙通道1
  "2,2": resolveHfdFnFuncDisplayKeyCode(8193, { v2: true }), // W 蓝牙通道2
  "2,3": resolveHfdFnFuncDisplayKeyCode(8194, { v2: true }), // E 蓝牙通道3
  "2,4": resolveHfdFnFuncDisplayKeyCode(8202, { v2: true }), // R 无线回连
  "2,13": resolveHfdFnFuncDisplayKeyCode(12288, { v2: true }), // \|（keycode 49）灯光效果切换
  "2,14": resolveHfdFnFuncDisplayKeyCode(27, { v2: false }), // PgUp 正面灯条效果切换
  // Row 3 — Caps / ASDF … Enter·PgDn
  "3,2": resolveHfdFnFuncDisplayKeyCode(8, { v2: false }), // S WIN
  "3,3": resolveHfdFnFuncDisplayKeyCode(9, { v2: false }), // D MAC
  "3,13": resolveHfdFnFuncDisplayKeyCode(12289, { v2: true }), // Enter 灯光颜色切换
  "3,14": resolveHfdFnFuncDisplayKeyCode(29, { v2: false }), // PgDn 正面灯条颜色切换
  // Row 4 — Shift / ZXCV … ↑（col12=keycode82，col13 为矩阵占位 0）
  "4,12": resolveHfdFnFuncDisplayKeyCode(12290, { v2: true }), // ↑ 灯光亮度+
  // Row 5 — 底行 / 方向键
  "5,1": resolveHfdFnFuncDisplayKeyCode(22, { v2: false }), // L-Win 锁定Win
  "5,10": resolveHfdFnFuncDisplayKeyCode(7, { v2: false }), // R-Alt 查询电量
  "5,12": resolveHfdFnFuncDisplayKeyCode(12293, { v2: true }), // ← 灯光速度-
  "5,13": resolveHfdFnFuncDisplayKeyCode(12291, { v2: true }), // ↓ 灯光亮度-
  "5,14": resolveHfdFnFuncDisplayKeyCode(12292, { v2: true }), // → 灯光速度+
};

const X98_1K_BASE_MATRIX = KEYCODE_MAP_HFD_LAYOUT.huafenda98;

function readX98_1KEquipmentFlag(): boolean {
  try {
    const raw = localStorage.getItem("equipmentData");
    if (!raw) return false;
    const eq = JSON.parse(raw) as {
      displayname?: string;
      productName?: string;
      keyboardType?: string;
      solutionRovider?: string;
    };
    if (String(eq?.solutionRovider ?? "").trim() !== "huafenda") {
      return false;
    }
    const name = String(eq?.displayname ?? eq?.productName ?? "").trim();
    return (
      name === "X98 (1K)" ||
      name.startsWith("X98 (1K)") ||
      String(eq?.keyboardType ?? "") === "huafenda98"
    );
  } catch {
    return false;
  }
}

export function buildHuafendaX98_1KFnLayerKeyCodes(): number[][] {
  return X98_1K_BASE_MATRIX.map((row, rowIndex) =>
    row.map((baseCell, col) => {
      const overrideKey = `${rowIndex},${col}`;
      if (
        Object.prototype.hasOwnProperty.call(
          X98_1K_FN_CELL_OVERRIDES,
          overrideKey
        )
      ) {
        return X98_1K_FN_CELL_OVERRIDES[overrideKey];
      }
      if (baseCell === 0) return 0;
      return baseCell;
    })
  );
}

export function applyHuafendaX98_1KFnLayerToSdkRows(
  rows: { layer: number; row: number; keyboardLayout: number[] }[]
): { layer: number; row: number; keyboardLayout: number[] }[] {
  const fnMatrix = buildHuafendaX98_1KFnLayerKeyCodes();
  return rows.map((row) => {
    const staticRow = fnMatrix[row.row];
    if (!staticRow) return row;
    return { ...row, keyboardLayout: [...staticRow] };
  });
}

/** 华奋达 X98 (1K)（huafenda98）Fn 层使用静态出厂映射表 */
export function shouldApplyHuafendaX98_1KFnLayer(
  keyboardType: string,
  layer: number
): boolean {
  if (layer === 0) return false;
  if (keyboardType === "huafenda98") return true;
  return readX98_1KEquipmentFlag();
}

export function getHuafendaX98_1KFnLayerRow(rowIndex: number): number[] | null {
  const matrix = buildHuafendaX98_1KFnLayerKeyCodes();
  return matrix[rowIndex] ?? null;
}
